import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sprout, CheckCircle2, XCircle } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [validInfo, setValidInfo] = useState(null); // {email,name}
  const [tokenError, setTokenError] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      if (!token) { setTokenError("Link mancante."); setChecking(false); return; }
      try {
        const { data } = await axios.post(`${API}/auth/verify-reset-token`, { token });
        setValidInfo({ email: data.email, name: data.name });
      } catch (err) {
        setTokenError(formatApiErrorDetail(err.response?.data?.detail) || "Link non valido");
      } finally { setChecking(false); }
    })();
  }, [token]);

  async function onSubmit(e) {
    e.preventDefault();
    if (pwd.length < 8) { setError("Minimo 8 caratteri"); return; }
    if (pwd !== pwd2) { setError("Le due password non coincidono"); return; }
    setError(""); setSaving(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setSaved(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
    } finally { setSaving(false); }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-[#FDFBF7] p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#1C352D] grid place-items-center text-white"><Sprout size={20}/></div>
          <div>
            <div className="font-semibold text-[#1C352D]">RDM ClassRoom</div>
            <div className="text-[10px] text-stone-500 uppercase tracking-[0.25em]">Reset password</div>
          </div>
        </div>

        {checking && <div className="text-sm text-stone-500">Verifica del link in corso…</div>}

        {!checking && tokenError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800" data-testid="reset-invalid">
            <div className="flex items-center gap-2 font-medium mb-1"><XCircle size={18}/> Link non valido</div>
            <p>{tokenError}</p>
            <Link to="/forgot-password" className="mt-3 inline-block text-red-900 underline text-[13px]">Richiedi un nuovo link →</Link>
          </div>
        )}

        {!checking && !tokenError && !saved && (
          <>
            <h1 className="text-2xl font-semibold text-[#1C352D]">Scegli una nuova password</h1>
            <p className="text-sm text-stone-500 mt-2">Stai reimpostando la password per <b>{validInfo?.email}</b></p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Nuova password</Label>
                <Input type="password" required minLength={8}
                  data-testid="reset-pwd"
                  value={pwd} onChange={(e) => setPwd(e.target.value)}
                  className="mt-1.5 bg-white h-11 border-stone-200" placeholder="Minimo 8 caratteri"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Ripeti password</Label>
                <Input type="password" required minLength={8}
                  data-testid="reset-pwd2"
                  value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                  className="mt-1.5 bg-white h-11 border-stone-200"/>
              </div>
              {error && <div data-testid="reset-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
              <Button type="submit" data-testid="reset-submit" disabled={saving || !pwd || !pwd2}
                className="w-full h-12 rounded-full bg-[#1C352D] hover:bg-[#264A3E]">
                {saving ? "Salvataggio…" : "Salva nuova password"}
              </Button>
            </form>
          </>
        )}

        {saved && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-sm text-emerald-900" data-testid="reset-saved">
            <div className="flex items-center gap-2 font-medium mb-1"><CheckCircle2 size={18}/> Password aggiornata</div>
            <p>Reindirizzamento al login…</p>
          </div>
        )}
      </div>
    </div>
  );
}
